/**
 * Organization Switcher Component
 * 
 * Allows users who belong to multiple organizations to switch between them
 * Displays current organization and provides a dropdown to select others
 */

import { useState, useEffect } from 'react';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Organization {
  id: string;
  name: string;
  org_role: 'owner' | 'super_user' | 'member';
}

export function OrganizationSwitcher() {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrgName, setCurrentOrgName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, [user]);

  const fetchOrganizations = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      // Get all organizations the user belongs to
      const { data: memberships, error: membershipsError } = await supabase
        .from('organization_user_roles')
        .select(`
          organization_id,
          org_role,
          organizations!organization_id (
            id,
            name
          )
        `)
        .eq('user_id', user.id);

      if (membershipsError) throw membershipsError;

      const orgs = (memberships || []).map((m: any) => ({
        id: m.organization_id,
        name: m.organizations?.name || 'Unknown Organization',
        org_role: m.org_role
      }));

      setOrganizations(orgs);

      // Get current organization name
      if (user.organization_id) {
        const currentOrg = orgs.find(o => o.id === user.organization_id);
        setCurrentOrgName(currentOrg?.name || 'Current Organization');
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchOrganization = async (orgId: string) => {
    if (orgId === user?.organization_id) return; // Already on this org

    try {
      setSwitching(true);

      const { data, error } = await supabase.rpc('switch_user_organization', {
        p_new_org_id: orgId
      });

      if (error) throw error;

      if (data && typeof data === 'object' && 'error' in data) {
        throw new Error((data as any).error);
      }

      toast({
        title: "Organization Switched",
        description: "Refreshing to load new organization...",
      });

      // Refresh user session and reload page
      if (refreshUser) {
        await refreshUser();
      }
      
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to switch organization",
        variant: "destructive",
      });
    } finally {
      setSwitching(false);
    }
  };

  // Only show if user belongs to multiple organizations
  if (loading || organizations.length <= 1) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className="gap-2"
          disabled={switching}
        >
          <Building2 className="h-4 w-4" />
          <span className="max-w-[150px] truncate">{currentOrgName}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[280px]">
        <DropdownMenuLabel>Switch Organization</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {organizations.map((org) => (
          <DropdownMenuItem
            key={org.id}
            onClick={() => handleSwitchOrganization(org.id)}
            disabled={switching || org.id === user?.organization_id}
            className="cursor-pointer"
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-start flex-col">
                <span className="font-medium">{org.name}</span>
                <span className="text-xs text-muted-foreground capitalize">
                  {org.org_role === 'super_user' ? 'Super User' : org.org_role}
                </span>
              </div>
              {org.id === user?.organization_id && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
